import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ReadyToShipPage from './pages/ReadyToShipPage';
import WillCallPage from './pages/WillCallPage';
import TrackingPage from './pages/TrackingPage';
import ScanInPage from './pages/ScanInPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import PartsOrdersPage from './pages/PartsOrdersPage';
import ShipmentsPage from './pages/ShipmentsPage';
import ConsolidationsPage from './pages/ConsolidationsPage';
import ShippingStationsPage from './pages/ShippingStationsPage';
import ShippingQueuePage from './pages/ShippingQueuePage';
import ShippingWorkCardPage from './pages/ShippingWorkCardPage';
import BayManagementPage from './pages/BayManagementPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="ready-to-ship" element={<ReadyToShipPage />} />
        <Route path="shipments" element={<ShipmentsPage />} />
        <Route path="consolidations" element={<ConsolidationsPage />} />
        <Route path="parts-orders" element={<PartsOrdersPage />} />
        <Route path="will-call" element={<WillCallPage />} />
        <Route path="scan-in" element={<ScanInPage />} />
        <Route path="stations" element={<ShippingStationsPage />} />
        <Route path="stations/:workstationId" element={<ShippingQueuePage />} />
        <Route path="stations/:workstationId/card/:workOrderId" element={<ShippingWorkCardPage />} />
        <Route path="tracking" element={<TrackingPage />} />
        <Route path="bays" element={<BayManagementPage />} />
        <Route
          path="reports"
          element={
            <AdminRoute>
              <ReportsPage />
            </AdminRoute>
          }
        />
        <Route
          path="settings"
          element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
